import { Controller } from "@hotwired/stimulus"
import * as maptilersdk from '@maptiler/sdk'

export default class extends Controller {
  static values = {
    apiKey: String,
    center: Array,
    zoom: Number
  }

  static targets = ["tooltip"]

  static CONFIG = {
    DEFAULT_CENTER: [3.40, 45.20],
    DEFAULT_ZOOM: 6.5,
    COLORS: {
      DEFAULT: '#1F2648',
      ACTIVE: '#FAB313'
    },
    LINE_WIDTH: 5,
    SELECTED_LINE_WIDTH: 3
  }

  async connect() {
    try {
      // Chargement dynamique de la bibliothèque MapTiler seulement quand on en a besoin
      const maptilersdk = await import('@maptiler/sdk');

      // Configurer la clé API après l'importation
      maptilersdk.config.apiKey = this.apiKeyValue;

      // Initialiser la carte et le tooltip
      this.initializeMap();
      this.initializeTooltip();
    } catch (error) {
      console.error("Error in connect:", error);
    }
  }

  initializeTooltip() {
    const tooltipDiv = document.createElement('div');
    tooltipDiv.className = 'absolute left-1/2 bottom-8 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg text-base text-blue font-medium hidden';
    tooltipDiv.setAttribute('data-home-map-panel-target', 'tooltip');
    this.element.appendChild(tooltipDiv);
  }

  showTooltip(text) {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.textContent = text;
      this.tooltipTarget.classList.remove('hidden');
    }
  }

  hideTooltip() {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.add('hidden');
    }
  }

  initializeMap() {
    const defaultCenter = this.constructor.CONFIG.DEFAULT_CENTER;
    const center = this.hasCenterValue ? this.centerValue : defaultCenter;

    this.map = new maptilersdk.Map({
      container: this.element,
      style: '91479711-437a-4607-a18f-a357212e1c52',
      center: center,
      zoom: this.zoomValue || this.constructor.CONFIG.DEFAULT_ZOOM,
      minZoom: 5,
      maxZoom: 15,
      dragPan: false,
      dragRotate: false,
      scrollZoom: false,
      touchZoom: false,
      doubleClickZoom: false,
      touchPitch: false,
      keyboard: false,
      attributionControl: false,
      navigationControl: false,
      geolocateControl: false,
      fullscreenControl: false,
      scaleControl: false
    });

    this.map.on('load', () => {
      this.loadPaths();
    });
  }

  async loadPaths() {
    try {
      console.log('=== LOAD PATHS DÉBUT ===');
      
      const response = await fetch('/api/way_itineraries.json');
      if (!response.ok) throw new Error('Network response was not ok');
      const geojson = await response.json();
      
      console.log('GeoJSON reçu de l\'API:', geojson);
      console.log('Nombre de features:', geojson.features.length);
      
      // Afficher des informations sur chaque feature
      geojson.features.forEach((feature, index) => {
        console.log(`Feature #${index} - ID: ${feature.properties.id}, Nom: ${feature.properties.name}`);
        console.log(`  Type de géométrie: ${feature.geometry.type}`);
        console.log(`  Nombre de coordonnées: ${feature.geometry.coordinates.length}`);
      });

      // Stocker les données GeoJSON pour référence ultérieure
      this.pathsData = geojson;
      
      // Créer un cache des features indexé par ID
      this.featuresById = {};
      geojson.features.forEach(feature => {
        const id = feature.properties.id;
        // Stocker une copie profonde pour éviter les modifications accidentelles
        this.featuresById[id] = JSON.parse(JSON.stringify(feature));
      });
      
      console.log('Cache des features créé:', Object.keys(this.featuresById).length, 'features indexés');
      console.log('Données GeoJSON stockées dans this.pathsData');

      // Source unique pour tous les chemins
      this.map.addSource('paths', {
        type: 'geojson',
        data: geojson
      });
      console.log('Source paths ajoutée à la carte');

      // Layer principal visible - tous les chemins en bleu
      this.map.addLayer({
        id: 'paths-line',
        type: 'line',
        source: 'paths',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': 'visible'
        },
        paint: {
          'line-color': this.constructor.CONFIG.COLORS.DEFAULT,
          'line-width': this.constructor.CONFIG.LINE_WIDTH,
          'line-opacity': 1
        }
      });

      // Source vide pour le hover
      this.map.addSource('hover-path', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // Layer de hover - chemin survolé en jaune
      this.map.addLayer({
        id: 'hover-path-line',
        type: 'line',
        source: 'hover-path',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': 'visible'
        },
        paint: {
          'line-color': this.constructor.CONFIG.COLORS.ACTIVE,
          'line-width': this.constructor.CONFIG.SELECTED_LINE_WIDTH,
          'line-opacity': 1
        }
      });

      // Source vide pour la sélection
      this.map.addSource('selected-path', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // Layer de sélection - chemin sélectionné en jaune
      this.map.addLayer({
        id: 'selected-path-line',
        type: 'line',
        source: 'selected-path',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': 'visible'
        },
        paint: {
          'line-color': this.constructor.CONFIG.COLORS.ACTIVE,
          'line-width': this.constructor.CONFIG.SELECTED_LINE_WIDTH,
          'line-opacity': 1
        }
      });

      this.addCityMarkers(geojson.features);

      // Variable pour stocker l'ID de la trace survolée
      this.hoveredPathId = null;

      // Événements d'interaction
      this.map.on('click', 'paths-line', (e) => {
        if (e.features.length > 0) {
          const feature = e.features[0];
          if (this.isPathSelected(feature)) {
            this.clearSelection();
            const closeButton = document.querySelector(`[data-home-way-panel-target="wayDetails"][data-path-id="${feature.properties.id}"] [data-action="click->home-way-panel#closeDetails"]`);
            if (closeButton) closeButton.click();
          } else {
            this.selectPath(feature);
            this.showPathDetails(feature.properties.id);
          }
        }
      });

      this.map.on('mousemove', 'paths-line', (e) => {
        if (e.features.length > 0) {
          const feature = e.features[0];
          const pathId = feature.properties.id;
          
          // Si on survole déjà cette trace, ne rien faire
          if (this.hoveredPathId === pathId) return;
          
          // Mettre à jour l'ID de la trace survolée
          this.hoveredPathId = pathId;
          
          this.map.getCanvas().style.cursor = 'pointer';
          if (!this.isPathSelected(feature)) {
            // Effacer le highlight précédent
            this.clearPathHighlight();
            
            // Appliquer le nouveau highlight après un court délai
            setTimeout(() => {
              if (this.hoveredPathId === pathId) {
                this.highlightPath(feature);
                this.highlightButton(feature.properties.id);
                this.showTooltip(feature.properties.name);
              }
            }, 10);
          }
        }
      });

      this.map.on('mouseleave', 'paths-line', () => {
        this.map.getCanvas().style.cursor = '';
        this.hoveredPathId = null;
        this.clearPathHighlight();
        this.clearButtonHighlight();
        if (this.selectedFeature) {
          this.showTooltip(this.selectedFeature.properties.name);
        } else {
          this.hideTooltip();
        }
      });

      console.log('=== LOAD PATHS FIN ===');
    } catch (error) {
      console.error("Error loading paths:", error);
    }
  }

  isPathSelected(feature) {
    return this.selectedFeature && this.selectedFeature.properties.id === feature.properties.id;
  }

  selectPath(feature) {
    console.log('=== SELECT PATH DÉBUT ===');
    console.log('Feature sélectionné:', feature);
    console.log('Feature ID:', feature.properties.id);
    
    try {
      // Stocker le feature sélectionné (copie profonde)
      this.selectedFeature = {
        type: 'Feature',
        geometry: JSON.parse(JSON.stringify(feature.geometry)),
        properties: JSON.parse(JSON.stringify(feature.properties))
      };
      
      console.log('Feature copié et stocké dans this.selectedFeature');
      
      const source = this.map.getSource('selected-path');
      if (source) {
        // Créer un nouveau GeoJSON avec uniquement cette trace
        const singleFeatureCollection = {
          type: 'FeatureCollection',
          features: [this.selectedFeature]
        };
        
        console.log('Nouveau GeoJSON créé pour la sélection');
        console.log('Nombre de coordonnées:', this.selectedFeature.geometry.coordinates.length);
        
        // Mettre à jour la source de sélection avec uniquement cette trace
        source.setData(singleFeatureCollection);
        console.log('Source de sélection mise à jour');
      } else {
        console.log('Source selected-path non trouvée');
      }
      
      this.showTooltip(feature.properties.name);
    } catch (error) {
      console.error('Erreur lors de la sélection du chemin:', error);
    }
    
    console.log('=== SELECT PATH FIN ===');
  }

  clearSelection() {
    this.selectedFeature = null;
    const source = this.map.getSource('selected-path');
    if (source) {
      // Vider la source de sélection
      source.setData({
        type: 'FeatureCollection',
        features: []
      });
    }
    this.hideTooltip();
  }

  addCityMarkers(features) {
    features.forEach(feature => {
      const { city_start, city_end } = feature.properties;

      if (city_start) {
        this.createMarker(city_start.latitude, city_start.longitude);
      }

      if (city_end) {
        this.createMarker(city_end.latitude, city_end.longitude);
      }
    });
  }

  createMarker(latitude, longitude) {
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-marker';
    markerElement.style.width = '12px';
    markerElement.style.height = '12px';
    markerElement.style.backgroundColor = '#fab313';
    markerElement.style.borderRadius = '50%';
    markerElement.style.border = '2px solid #000';
    markerElement.style.boxSizing = 'border-box';

    return new maptilersdk.Marker({ element: markerElement })
      .setLngLat([longitude, latitude])
      .addTo(this.map);
  }

  highlightPath(feature) {
    console.log('=== HIGHLIGHT PATH DÉBUT ===');
    console.log('Feature survolé:', feature);
    console.log('Feature ID:', feature.properties.id);
    console.log('Feature geometry type:', feature.geometry.type);
    console.log('Feature coordinates length:', feature.geometry.coordinates.length);
    
    const source = this.map.getSource('hover-path');
    if (source) {
      console.log('Source hover-path trouvée');
      
      // Récupérer l'ID de la trace survolée
      const traceId = feature.properties.id;
      console.log('Trace ID:', traceId);
      
      try {
        // Créer un nouveau GeoJSON avec uniquement cette trace
        // Utiliser une copie profonde pour éviter de modifier l'original
        const singleFeature = {
          type: 'Feature',
          geometry: JSON.parse(JSON.stringify(feature.geometry)),
          properties: JSON.parse(JSON.stringify(feature.properties))
        };
        
        const singleFeatureCollection = {
          type: 'FeatureCollection',
          features: [singleFeature]
        };
        
        console.log('Nouveau GeoJSON créé:', singleFeatureCollection);
        console.log('Nombre de features:', singleFeatureCollection.features.length);
        console.log('Nombre de coordonnées:', singleFeatureCollection.features[0].geometry.coordinates.length);
        
        // Mettre à jour la source de hover avec uniquement cette trace
        source.setData(singleFeatureCollection);
        console.log('Source mise à jour avec le nouveau GeoJSON');
      } catch (error) {
        console.error('Erreur lors de la création du GeoJSON:', error);
      }
    } else {
      console.log('Source hover-path non trouvée');
    }
    
    console.log('=== HIGHLIGHT PATH FIN ===');
  }

  clearPathHighlight() {
    console.log('=== CLEAR PATH HIGHLIGHT DÉBUT ===');
    
    const source = this.map.getSource('hover-path');
    if (source) {
      console.log('Source hover-path trouvée, vidage de la source');
      // Vider la source de hover
      source.setData({
        type: 'FeatureCollection',
        features: []
      });
      console.log('Source vidée');
    } else {
      console.log('Source hover-path non trouvée');
    }
    
    console.log('=== CLEAR PATH HIGHLIGHT FIN ===');
  }

  highlightButton(pathId) {
    const button = document.querySelector(`[data-path-id="${pathId}"]`);
    if (button) {
      button.classList.add('!bg-yellow-500', '!border-yellow-600');
    }
  }

  clearButtonHighlight() {
    const buttons = document.querySelectorAll('[data-path-id]');
    buttons.forEach(button => {
      button.classList.remove('!bg-yellow-500', '!border-yellow-600');
    });
  }

  showPathDetails(pathId) {
    const button = document.querySelector(`[data-path-id="${pathId}"]`);
    const details = document.querySelector(`[data-home-way-panel-target="wayDetails"][data-path-id="${pathId}"]`);
    const buttonsList = document.querySelector('[data-home-way-panel-target="buttonsList"]');
    
    if (button && details && buttonsList) {
      if (details.classList.contains('hidden')) {
        button.click();
      } else {
        this.clearSelection();
        const closeButton = details.querySelector('[data-action="click->home-way-panel#closeDetails"]');
        if (closeButton) closeButton.click();
      }
    }
  }

  findFeatureById(pathId, mapController) {
    const source = mapController.map.getSource('paths');
    if (!source) return null;

    const data = source._data;
    return data.features.find(f => f.properties.id === parseInt(pathId));
  }

  // Nouvelle méthode pour mettre en évidence une trace par son ID
  highlightPathById(pathId) {
    console.log(`=== HIGHLIGHT PATH BY ID: ${pathId} ===`);
    
    // Convertir en nombre si c'est une chaîne
    const id = parseInt(pathId, 10);
    
    // Récupérer la feature depuis le cache
    const feature = this.featuresById[id];
    
    if (feature) {
      console.log('Feature trouvé dans le cache:', feature.properties.name);
      
      // Créer un nouveau GeoJSON avec uniquement cette trace
      const singleFeature = {
        type: 'Feature',
        geometry: JSON.parse(JSON.stringify(feature.geometry)),
        properties: JSON.parse(JSON.stringify(feature.properties))
      };
      
      // Mettre à jour la source de hover
      const source = this.map.getSource('hover-path');
      if (source) {
        const singleFeatureCollection = {
          type: 'FeatureCollection',
          features: [singleFeature]
        };
        
        console.log('Mise à jour de la source hover-path avec le feature ID:', id);
        source.setData(singleFeatureCollection);
      }
      
      return feature;
    } else {
      console.log(`Aucun feature trouvé dans le cache avec l'ID: ${id}`);
      
      // Fallback à la méthode de recherche originale si le feature n'est pas dans le cache
      return this.findFeatureById(id, this);
    }
  }

  // Nouvelle méthode pour sélectionner une trace par son ID
  selectPathById(pathId) {
    console.log(`=== SELECT PATH BY ID: ${pathId} ===`);
    
    // Convertir en nombre si c'est une chaîne
    const id = parseInt(pathId, 10);
    
    // Récupérer la feature depuis le cache
    const feature = this.featuresById[id];
    
    if (feature) {
      console.log('Feature trouvé dans le cache pour sélection:', feature.properties.name);
      
      // Sélectionner la trace
      this.selectPath(feature);
      
      return feature;
    } else {
      console.log(`Aucun feature trouvé dans le cache avec l'ID: ${id} pour sélection`);
      
      // Fallback à la méthode de recherche originale si le feature n'est pas dans le cache
      const foundFeature = this.findFeatureById(id, this);
      if (foundFeature) {
        this.selectPath(foundFeature);
      }
      return foundFeature;
    }
  }
}
